// Öffentliche Seite zum Signierlink einer Vollmacht (ohne Login).
//
// Der Token erlaubt genau: dieses eine PDF ansehen/herunterladen und — wenn
// ein Signaturdienst eingerichtet ist — dieses PDF digital unterschreiben.
// Die PDF-Ansicht läuft über ein <iframe> mit Blob-URL, bewusst ohne react-pdf/
// pdfjs (Bundle und jsdom-Tests).

import * as React from "react";
import { useParams } from "react-router";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { REFINED } from "../components/risikoanalyseStep/tokens";
import { blobHerunterladen, vollmachtService } from "../services/vollmachtService";

const useStyles = makeStyles({
  seite: {
    minHeight: "100vh",
    backgroundColor: REFINED.fieldBg,
    fontFamily: REFINED.fontUI,
    color: REFINED.ink,
    ...shorthands.padding("24px", "16px"),
    boxSizing: "border-box",
  },
  karte: {
    maxWidth: "860px",
    marginLeft: "auto",
    marginRight: "auto",
    backgroundColor: REFINED.white,
    ...shorthands.borderRadius("14px"),
    boxShadow: REFINED.cardShadow,
    ...shorthands.padding("24px"),
    display: "flex",
    flexDirection: "column",
    rowGap: "14px",
  },
  eyebrow: { fontSize: `${REFINED.fs.xxs}px`, letterSpacing: "0.08em", textTransform: "uppercase", color: REFINED.aub, fontWeight: 700 },
  titel: { fontSize: `${REFINED.fs.xl}px`, fontWeight: 700, ...shorthands.margin("0") },
  text: { fontSize: `${REFINED.fs.md}px`, color: REFINED.ink2, ...shorthands.margin("0") },
  klein: { fontSize: `${REFINED.fs.sm}px`, color: REFINED.ink3 },
  rahmen: {
    width: "100%",
    height: "70vh",
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
  },
  aktionen: { display: "flex", flexWrap: "wrap", columnGap: "10px", rowGap: "10px" },
  primaer: {
    ...shorthands.border("0"),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.aub,
    color: REFINED.white,
    fontWeight: 700,
    fontSize: `${REFINED.fs.md}px`,
    ...shorthands.padding("10px", "18px"),
    cursor: "pointer",
    boxShadow: REFINED.btnShadow,
    ":disabled": { opacity: 0.6, cursor: "default" },
  },
  sekundaer: {
    ...shorthands.border("1.5px", "solid", REFINED.aub),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.white,
    color: REFINED.aub,
    fontWeight: 600,
    fontSize: `${REFINED.fs.md}px`,
    ...shorthands.padding("9px", "16px"),
    cursor: "pointer",
  },
  fehler: { color: REFINED.err, fontSize: `${REFINED.fs.sm}px` },
});

type Daten = Awaited<ReturnType<typeof vollmachtService.oeffentlich>>;

const PublicVollmachtUnterschreiben: React.FC = () => {
  const s = useStyles();
  const { token = "" } = useParams<{ token: string }>();
  const [daten, setDaten] = React.useState<Daten | null>(null);
  const [fehler, setFehler] = React.useState<string | null>(null);
  const [pdf, setPdf] = React.useState<{ blob: Blob; url: string } | null>(null);
  const [weiter, setWeiter] = React.useState(false);
  const [aktionsFehler, setAktionsFehler] = React.useState<string | null>(null);

  React.useEffect(() => {
    let lebt = true;
    let url: string | null = null;
    (async () => {
      try {
        const d = await vollmachtService.oeffentlich(token);
        if (!lebt) return;
        setDaten(d);
        if (d.status === "offen") {
          const blob = await vollmachtService.oeffentlichesPdf(token);
          if (!lebt) return;
          url = URL.createObjectURL(blob);
          setPdf({ blob, url });
        }
      } catch {
        if (lebt) setFehler("Dieser Link ist ungültig oder abgelaufen. Bitte wenden Sie sich an Ihren Berater.");
      }
    })();
    return () => {
      lebt = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [token]);

  const unterschreiben = async () => {
    setWeiter(true);
    setAktionsFehler(null);
    try {
      const { signierUrl } = await vollmachtService.unterschreiben(token);
      window.location.assign(signierUrl);
    } catch (err) {
      setAktionsFehler(err instanceof Error ? err.message : "Das hat nicht geklappt.");
      setWeiter(false);
    }
  };

  return (
    <div className={s.seite}>
      <main className={s.karte}>
        <div className={s.eyebrow}>Dokument zur Unterschrift</div>
        {fehler && <p className={s.text}>{fehler}</p>}
        {!fehler && !daten && <p className={s.text}>Lädt…</p>}
        {daten?.status === "erledigt" && (
          <>
            <h1 className={s.titel}>{daten.titel}</h1>
            <p className={s.text}>Vielen Dank — dieses Dokument liegt uns bereits unterschrieben vor.</p>
          </>
        )}
        {daten?.status === "offen" && (
          <>
            <h1 className={s.titel}>{daten.titel}</h1>
            <p className={s.text}>
              für {daten.kontaktName} · {daten.maklerName}
            </p>
            {pdf ? (
              <iframe className={s.rahmen} src={pdf.url} title={daten.titel} />
            ) : (
              <p className={s.klein}>Dokument wird geladen…</p>
            )}
            <div className={s.aktionen}>
              {daten.digitalMoeglich && (
                <button type="button" className={s.primaer} onClick={() => void unterschreiben()} disabled={weiter}>
                  {weiter ? "Weiterleitung…" : "Jetzt digital unterschreiben"}
                </button>
              )}
              <button
                type="button"
                className={s.sekundaer}
                disabled={!pdf}
                onClick={() => pdf && blobHerunterladen(pdf.blob, `${daten.titel}.pdf`)}
              >
                Herunterladen
              </button>
            </div>
            {!daten.digitalMoeglich && (
              <p className={s.text}>Bitte ausdrucken, unterschreiben und an uns zurücksenden.</p>
            )}
            {aktionsFehler && <p className={s.fehler}>{aktionsFehler}</p>}
            <p className={s.klein}>
              Link gültig bis {new Date(daten.ablauf).toLocaleDateString("de-DE")}.
            </p>
          </>
        )}
      </main>
    </div>
  );
};

export default PublicVollmachtUnterschreiben;
